/* Copyright (c) 2020-present CloudIO Inc */

import { isEmpty } from 'utils/isEmpty';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { isNotEmpty } from '../../utils/isEmpty';
import { Select, defaultValue } from './Select';
import { analyzeSelect } from './SelectAnalyze';

const SelectManager: ComponentManagerFn<'Select'> = (
  platform: Platform,
): ComponentManager<'Select'> => {
  return {
    ...baseComponentManager<'Select'>(platform, 'Select'),
    type: 'Select',
    name: 'Select',
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'optionValues':
          return isEmpty(itemData.comboDatasourceAlias);
        case 'comboDatasourceAlias':
          return isEmpty(itemData.optionValues.value);
        case 'valueAttribute':
        case 'labelAttribute':
        case 'doNotPrefetch':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'limitTags':
        case 'delimiter':
        case 'hideSelectedOptions':
          return itemData.multi;
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'EndIcon',
          'SelectReturnValueMapping',
          'StartIcon',
          'TriggerOnChange',
          'TriggerOnClick',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: (props) => {
          const multi = (props.props as Required<Select>).multi;
          return multi
            ? ['String']
            : [
                'Decimal',
                'Double',
                'Email',
                'Integer',
                'Reference',
                'String',
                'UserID',
                'YN',
                'TF',
              ];
        },
      },
      optionValues: {
        type: 'options',
        label: 'Option Values',
        category: 'Select Options',
        helpText:
          'If the options are fixed, then you can specify here instead of providing a Data Store Alias below. e.g. B:Blue, G:Green, Yellow, Red',
      },
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Select Options',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore'],
        helpText:
          'Only CloudDataStore & ReadOnlyDataStore allowed. Use Query Select action to query it externally.',
        required: (item: RequiredItem<'Select'>) =>
          isEmpty(item.props.optionValues.value),
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Select Options',
        getAttributeDataTypes: () => 'ALL',
        required: (item: RequiredItem<'Select'>) =>
          !!item.props.comboDatasourceAlias,
      },
      labelAttribute: {
        aliasType: 'LOV',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        category: 'Select Options',
        type: 'ComboAttributeChooser',
        required: (item: RequiredItem<'Select'>) =>
          !!item.props.comboDatasourceAlias,
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Select Options',
      },
      optionsWidth: {
        label: 'Options Width',
        type: 'number',
        category: 'Select Options',
        min: 0,
        max: 1000,
        step: 50,
      },
      multi: {
        label: 'Multi Selection',
        type: 'boolean',
        category: 'Select Options',
      },
      hideSelectedOptions: {
        label: 'Hide Selected Options',
        type: 'boolean',
        category: 'Select Options',
        helpText:
          'Enable the option to hide the selected items from the select dropdown.',
      },
      limitTags: {
        label: 'Limit Tags',
        type: 'numberInput',
        category: 'Select Options',
      },
      delimiter: {
        label: 'Delimiter',
        type: 'string',
        category: 'Select Options',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      unknownValueMessage: {
        label: 'Unknown Value Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Unknown Value Error Message',
        category: 'Data',
      },
      selectOptionOnBlur: {
        label: 'Select Option On Blur',
        type: 'boolean',
        category: 'Select Options',
        helpText: 'Enable the option to select the topmost option on Blur.',
      },
      selectOptionOnTab: {
        label: 'Select Option On Tab',
        type: 'boolean',
        category: 'Select Options',
        helpText: 'Enable the option to select the topmost option on Tab.',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    category: 'Forms',
    getDisplayText: (item) => item.props.viewAttribute,
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    excludeChildren: true,
    replaceableWith: ['RadioBox', 'TextField', 'LookupSelect'],
    onReplace: (item, fromItem) => {
      if (fromItem.type === 'RadioBox') {
        item.props.optionValues = fromItem.props.options;
      }
      return item;
    },
    analyze: analyzeSelect,
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The Select component provides a standard dropdown menu allowing users to pick from a list of predefined options. Unlike the **LOV Field** which is for massive datasets, the Select component is optimized for smaller, finite lists (like "State", "Color", or "Priority"). It supports both static hardcoded options and dynamic lists loaded from a Data Store.
Use this component for compact option selection in forms, menus, or filters.

**1. Data Binding**<br/>
The component binds to a field in your Data Store where the selection is saved.
* **View Attribute:** The form field that stores the selected value.
* **Supported Types:** String, Integer, Decimal, Reference, Email, UserID, **YN** (Yes/No), **TF** (True/False).
* **Multi-Select:** If **Multi Selection** is enabled, the attribute must be a **String**.

**2. Options Source**<br/>
You can populate the dropdown list in two ways:
* **A. Static Options (optionValues):** Define a comma-separated list directly. 
    * **Simple:** "Red, Green, Blue" (Value saved = Display text).
    * **Key-Value:** "R:Red, G:Green, B:Blue" (User sees "Red", System saves "R").
* **B. Dynamic Data Store (comboDatasourceAlias):** Link to a **CloudDataStore** or **ReadOnlyDataStore**.
    * **Value Attribute:** The column to save (e.g., "CountryCode").
    * **Label Attribute:** The column to display (e.g., "CountryName").
    * **Do Not Prefetch:** Improves performance by waiting to load the list until the user clicks the dropdown.

**3. Multi-Select Configuration**<br/>
You can allow users to pick more than one option at a time.
* **Multi Selection:** A boolean toggle.
* **Delimiter:** The character used to separate multiple values in the database (e.g., "," or ";").
* **Limit Tags:** Controls how many selected items are visible as "chips" before collapsing into a "+2 more" summary.
* **Hide Selected Options:** Prevents duplicate selections by removing chosen items from the list.

**4. Usability Enhancements**<br/>
* **Select Option On Tab / Blur:** Automatically selects the highlighted option when the user moves focus away, speeding up keyboard entry.
* **Unknown Value Message:** Defines a custom error message if the field holds legacy data not found in the current options.
* **Options Width:** Manually set the pixel width of the dropdown menu if the default auto-sizing is insufficient.

**5. Best Use Cases**<br/>
* **Simple Enums:** Selecting "Day of Week" or "Gender" using static options.
* **Reference Lists:** Selecting an "Account Manager" from a dynamic list of employees.
* **Tagging:** Selecting multiple "Skills" (Java, Python, SQL) for a user profile.
* **Configuration:** Selecting a "Theme" (Light/Dark) for user preferences.`,
    helpRenders: [
      {
        title: 'Default Select Field',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        visibleProps: [],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'Select',
          parent: 'form',
        },
      },
      {
        title: 'Static Options',
        description: 'With simple values seperated by comma.',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['optionValues'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Color' },
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
            optionValues: { type: 'text', value: 'Green, Yellow, Red' },
          },
          children: [],
          type: 'Select',
          parent: 'form',
        },
      },
      {
        title: 'Static Options',
        description: 'Static options with different key & labels',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['optionValues'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Color' },
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
            optionValues: { type: 'text', value: 'G:Green,Y:Yellow,R:Red' },
          },
          children: [],
          type: 'Select',
          parent: 'form',
        },
      },
      {
        title: 'Required',
        description:
          "Required prop renders an asterisk next to the label indicating it's mandatory.",
        page: SamplePage,
        exampleId: 'e4',
        height: 260,
        visibleProps: ['required'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Color' },
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
            optionValues: { type: 'text', value: 'G:Green,Y:Yellow,R:Red' },
            required: true,
          },
          children: [],
          type: 'Select',
          parent: 'form',
        },
      },
      {
        title: 'Readonly',
        description:
          'Turning off the insertable prop will disable the control while entering a new row. Updatable prop will disable editing an existing row.',
        page: SamplePage,
        exampleId: 'e6',
        height: 260,
        visibleProps: ['insertable'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Color' },
            viewAttribute: 'name',
            flex: {
              all: 12,
            },
            insertable: { type: 'boolean', value: false },
            optionValues: { type: 'text', value: 'G:Green,Y:Yellow,R:Red' },
          },
          children: [],
          type: 'Select',
          parent: 'form',
        },
      },
    ],
  };
};

export default SelectManager;
